import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { FAQS, PAGES, INFO_MENU } from "@/lib/siteContent";
import { SiteFooter } from "@/components/SiteFooter";
import { SupportTicketForm } from "@/components/SupportTicketForm";
import { CareersBoard } from "@/components/CareersBoard";

const EMAIL_RX = /([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/gi;
const Linkify = ({ text }) => text.split(EMAIL_RX).map((part, i) => (i % 2
  ? <a key={i} href={`mailto:${part}`} className="text-gold-200 hover:text-gold-100 underline-offset-2 hover:underline">{part}</a>
  : part));

const Item = ({ item, onNav }) => (typeof item === "string" ? <Linkify text={item} /> : (
  <>{item.pre}<button data-testid={`inline-link-${item.page}`} onClick={() => onNav?.(item.page)} className="text-gold-200 hover:text-gold-100 hover:underline underline-offset-2">{item.label}</button>{item.post}</>
));

const Block = ({ block, onNav }) => {
  if (typeof block === "string") {
    return <p className="font-read text-slate-300 leading-relaxed"><Linkify text={block} /></p>;
  }
  if (block && block.list) {
    return (
      <ul className="space-y-1.5 pl-1">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 font-read text-slate-300 leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold-300 shrink-0" />
            <span><Item item={item} onNav={onNav} /></span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const FaqContent = () => {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="faq-content">
      <h1 className="font-display font-medium text-3xl sm:text-4xl text-slate-100">Frequently asked questions</h1>
      <p className="mt-3 font-read text-slate-400">Everything you need to know about creating and publishing with KLYR Studio.</p>
      <div className="mt-8 divide-y divide-gold-500/12 border-y border-gold-500/12">
        {FAQS.map((f, i) => (
          <div key={i}>
            <button
              data-testid={`faq-q-${i}`}
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 py-5 text-left"
            >
              <span className="font-ui text-base font-medium text-slate-100">{f.q}</span>
              <ChevronDown className={`w-5 h-5 text-slate-300 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && (
              <div className="pb-6 -mt-1 space-y-3 max-w-2xl">
                {f.a.map((b, j) => <Block key={j} block={b} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StaticContent = ({ page, onNav }) => {
  const data = PAGES[page];
  if (!data) return null;
  return (
    <div data-testid={`page-content-${page}`}>
      <h1 className="font-display font-medium text-3xl sm:text-4xl text-slate-100">{data.title}</h1>
      {data.intro && <p className="mt-3 font-read text-lg text-slate-400">{data.intro}</p>}
      <div className="mt-10 space-y-9 max-w-2xl">
        {data.sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-ui text-lg font-semibold text-slate-100 mb-3">{s.h}</h2>
            <div className="space-y-3">
              {s.body.map((b, j) => <Block key={j} block={b} onNav={onNav} />)}
            </div>
          </div>
        ))}
      </div>
      {data.widget === "support" && <SupportTicketForm />}
      {data.widget === "careers" && <CareersBoard />}
    </div>
  );
};

export const InfoPage = ({ initialPage = "faq", onHome, onGetStarted, onNavigateOther }) => {
  const [page, setPageState] = useState(initialPage);
  const setPage = (k) => {
    setPageState(k);
    if (window.location.pathname !== `/${k}`) window.history.pushState({}, "", `/${k}`);
  };

  useEffect(() => {
    setPageState(initialPage);
  }, [initialPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [page]);

  return (
    <div className="grain min-h-screen bg-navy-950 text-slate-100 flex flex-col">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 glass bg-navy-950/80 border-b border-gold-500/12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button data-testid="info-logo" onClick={onHome} className="text-left leading-none">
            <div className="klyr-word klyr-metal klyr-sheen text-2xl">KLYR</div>
            <div className="klyr-sub text-[8px] mt-0.5">STUDIO</div>
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="info-back-home"
              onClick={onHome}
              className="flex items-center gap-2 font-ui text-sm text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to {onGetStarted ? "home" : "app"}
            </button>
            {onGetStarted && (
              <button
                data-testid="info-start-free"
                onClick={onGetStarted}
                className="klyr-btn-metal font-ui text-sm font-semibold rounded-full px-5 py-2 transition-[filter]"
              >
                Start free
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-16">
          {/* Menu */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">Help & policies</div>
            <nav className="flex flex-wrap lg:flex-col gap-2">
              {INFO_MENU.map((m) => (
                <button
                  key={m.key}
                  data-testid={`info-menu-${m.key}`}
                  onClick={() => setPage(m.key)}
                  className={`font-ui text-sm text-left rounded-lg px-3 py-2 transition-colors ${
                    page === m.key
                      ? "bg-gold-400/15 text-slate-200 border border-gold-400/40"
                      : "text-slate-400 hover:text-slate-100 border border-transparent hover:border-gold-500/12"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <motion.div
            key={page}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {page === "faq" ? <FaqContent /> : <StaticContent page={page} onNav={setPage} />}
          </motion.div>
        </div>
      </main>

      <SiteFooter onNavigate={(k) => (PAGES[k] ? setPage(k) : onNavigateOther && onNavigateOther(k))} />
    </div>
  );
};
